import React, { useState } from 'react';
import { ArrowRight, Mail, Calendar, CheckCircle2, Loader2 } from 'lucide-react';

const DEMO_REQUEST_ENDPOINT =
  'https://us-central1-boreal-phoenix-405421.cloudfunctions.net/vibe-landing-demo-request';

const INITIAL_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  company: '',
  monthlyLoadVolume: '',
};

export default function CTA() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'submitting') return;

    setStatus('submitting');
    setError('');

    try {
      const res = await fetch(DEMO_REQUEST_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, test: false }),
      });

      if (!res.ok) {
        throw new Error(`Request failed with status ${res.status}`);
      }

      setStatus('success');
      setForm(INITIAL_FORM);
    } catch (err) {
      setStatus('error');
      setError(
        'Something went wrong while submitting your request. Please try again.'
      );
    }
  };

  const isSubmitting = status === 'submitting';

  return (
    <section id="demo" className="bg-white">
      <div className="container-page pb-20 lg:pb-28">
        <div className="relative overflow-hidden rounded-3xl bg-ink-900 text-white px-8 py-12 sm:px-12 sm:py-16">
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-[0.06] bg-dotted"
          />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] font-semibold text-white/60">
                Get started
              </p>
              <h2 className="mt-3 h-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-balance">
                Find out what you’d save on your next 1,000 loads.
              </h2>
              <p className="mt-5 text-white/75 text-lg max-w-xl text-balance">
                Book a 25-minute demo. Bring a sample of your lanes, and we’ll
                run them through Vibe Engine live — so you can see exactly
                where the savings come from.
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <a
                  href="#schedule"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-white text-ink-900 px-5 py-2.5 font-medium hover:bg-ink-100 transition-colors"
                >
                  <Calendar className="h-4 w-4" />
                  Book a demo
                  <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href="mailto:hello@vibeengine.io"
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/20 text-white px-5 py-2.5 font-medium hover:bg-white/10 transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  Talk to sales
                </a>
              </div>
            </div>

            {status === 'success' ? (
              <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-6 backdrop-blur flex flex-col items-center text-center">
                <CheckCircle2 className="h-12 w-12 text-emerald-400" />
                <h3 className="mt-4 text-xl font-semibold">Request received</h3>
                <p className="mt-2 text-white/70 text-sm max-w-sm">
                  Thanks for reaching out. Our team will be in touch shortly to
                  schedule your demo.
                </p>
                <button
                  type="button"
                  onClick={() => setStatus('idle')}
                  className="mt-5 inline-flex items-center justify-center gap-2 rounded-lg border border-white/20 text-white px-5 py-2.5 font-medium hover:bg-white/10 transition-colors"
                >
                  Submit another request
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="rounded-2xl bg-white/[0.04] border border-white/10 p-6 backdrop-blur"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field
                    label="First name"
                    name="firstName"
                    placeholder="Alex"
                    value={form.firstName}
                    onChange={handleChange}
                    required
                  />
                  <Field
                    label="Last name"
                    name="lastName"
                    placeholder="Morgan"
                    value={form.lastName}
                    onChange={handleChange}
                    required
                  />
                  <Field
                    label="Work email"
                    name="email"
                    type="email"
                    placeholder="alex@yourco.com"
                    className="sm:col-span-2"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                  <Field
                    label="Company"
                    name="company"
                    placeholder="Your company"
                    className="sm:col-span-2"
                    value={form.company}
                    onChange={handleChange}
                    required
                  />
                  <div className="sm:col-span-2">
                    <label className="text-xs uppercase tracking-wider font-semibold text-white/60">
                      Monthly load volume
                    </label>
                    <select
                      name="monthlyLoadVolume"
                      className="mt-1.5 w-full rounded-lg bg-white/[0.06] border border-white/15 text-white px-3.5 py-2.5 text-sm focus:outline-none focus:border-white/40"
                      value={form.monthlyLoadVolume}
                      onChange={handleChange}
                      required
                    >
                      <option value="" disabled className="text-ink-900">
                        Select a range…
                      </option>
                      <option value="Under 200" className="text-ink-900">
                        Under 200
                      </option>
                      <option value="200 – 1,000" className="text-ink-900">
                        200 – 1,000
                      </option>
                      <option value="1,000 – 5,000" className="text-ink-900">
                        1,000 – 5,000
                      </option>
                      <option value="5,000+" className="text-ink-900">
                        5,000+
                      </option>
                    </select>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-white text-ink-900 px-5 py-2.5 font-medium hover:bg-ink-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Submitting…
                    </>
                  ) : (
                    <>
                      Request my demo
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>

                {status === 'error' && (
                  <p className="mt-3 text-xs text-red-300">{error}</p>
                )}

                <p className="mt-3 text-xs text-white/50">
                  By submitting, you agree to be contacted about Vibe Engine. We
                  don’t share your data.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({
  label,
  name,
  placeholder,
  type = 'text',
  className = '',
  value,
  onChange,
  required = false,
}) {
  return (
    <div className={className}>
      <label className="text-xs uppercase tracking-wider font-semibold text-white/60">
        {label}
      </label>
      <input
        name={name}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        className="mt-1.5 w-full rounded-lg bg-white/[0.06] border border-white/15 text-white placeholder:text-white/30 px-3.5 py-2.5 text-sm focus:outline-none focus:border-white/40"
      />
    </div>
  );
}
